import {useCallback, useEffect, useId, useRef, useState} from 'react'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {clsx} from 'clsx'

import Styles from './BackgroundGrid.module.css'
import {
  CELL_SIZE_PX,
  getEvictedTrailPixelIds,
  getGridDimensions,
  getTrailGridPosition,
  shouldCreateTrailPixel,
  type GridPosition,
} from './background-grid-helpers'
import {BACKGROUND_COLOR, COLORS, GRID_LINE_COLOR} from './config'

const CELL_SIZE = 1
const TRAIL_EXIT_DURATION_MS = 100

type TrailPixel = {
  id: number
  x: number
  y: number
  color: string
  exiting: boolean
}

type TrailPixelTimeouts = {
  exit: number
  positionIndex: number
  removal: number
}

export type BackgroundGridProps = {
  className?: string
}

function randomColor() {
  return COLORS[Math.floor(Math.random() * COLORS.length)] ?? COLORS[0]
}

export default function BackgroundGrid({className}: BackgroundGridProps) {
  const backgroundGridId = useId().replace(/:/g, '')
  const gradientId = `background-grid-gradient-${backgroundGridId}`
  const patternId = `background-grid-pattern-${backgroundGridId}`
  const wrapperRef = useRef<HTMLDivElement>(null)
  const nextTrailPixelIdRef = useRef(0)
  const trailPixelIdsRef = useRef<number[]>([])
  const activeTrailPositionsRef = useRef<Set<number>>(new Set())
  const lastTrailPositionRef = useRef<GridPosition | null>(null)
  const trailTimeoutsRef = useRef<Map<number, TrailPixelTimeouts>>(new Map())
  const [trailPixels, setTrailPixels] = useState<TrailPixel[]>([])
  const [gridDimensions, setGridDimensions] = useState(() => getGridDimensions(0, 0))
  const {height: gridHeight, width: gridWidth} = gridDimensions
  const prefersReducedMotion = usePrefersReducedMotion()
  const trailContext = `${gridWidth}:${gridHeight}:${prefersReducedMotion}`
  const [previousTrailContext, setPreviousTrailContext] = useState(trailContext)

  if (previousTrailContext !== trailContext) {
    setPreviousTrailContext(trailContext)
    setTrailPixels([])
  }

  const clearTrailPixelTimeout = useCallback((id: number) => {
    const timeouts = trailTimeoutsRef.current.get(id)
    if (timeouts !== undefined) {
      window.clearTimeout(timeouts.exit)
      window.clearTimeout(timeouts.removal)
      activeTrailPositionsRef.current.delete(timeouts.positionIndex)
      trailTimeoutsRef.current.delete(id)
    }
  }, [])

  const clearTrail = useCallback(() => {
    for (const id of [...trailTimeoutsRef.current.keys()]) {
      clearTrailPixelTimeout(id)
    }
    trailPixelIdsRef.current = []
    activeTrailPositionsRef.current.clear()
    lastTrailPositionRef.current = null
  }, [clearTrailPixelTimeout])

  const removeTrailPixel = useCallback(
    (id: number) => {
      clearTrailPixelTimeout(id)
      trailPixelIdsRef.current = trailPixelIdsRef.current.filter(pixelId => pixelId !== id)
      setTrailPixels(currentPixels => currentPixels.filter(pixel => pixel.id !== id))
    },
    [clearTrailPixelTimeout],
  )

  const addTrailPixel = useCallback(
    (x: number, y: number) => {
      const positionIndex = y * gridWidth + x
      if (activeTrailPositionsRef.current.has(positionIndex)) {
        return
      }

      const id = nextTrailPixelIdRef.current++
      const exitDelay = 100 + 100 * Math.random()
      activeTrailPositionsRef.current.add(positionIndex)
      const exitTimeout = window.setTimeout(() => {
        setTrailPixels(currentPixels =>
          currentPixels.map(pixel => (pixel.id === id ? {...pixel, exiting: true} : pixel)),
        )
      }, exitDelay)
      const removalTimeout = window.setTimeout(() => {
        removeTrailPixel(id)
      }, exitDelay + TRAIL_EXIT_DURATION_MS)

      trailTimeoutsRef.current.set(id, {
        exit: exitTimeout,
        positionIndex,
        removal: removalTimeout,
      })
      trailPixelIdsRef.current.push(id)
      const evictedIds = getEvictedTrailPixelIds(trailPixelIdsRef.current)

      if (evictedIds.length > 0) {
        trailPixelIdsRef.current = trailPixelIdsRef.current.slice(evictedIds.length)
        for (const evictedId of evictedIds) {
          clearTrailPixelTimeout(evictedId)
        }
      }

      setTrailPixels(currentPixels => {
        const retainedPixels = evictedIds.length
          ? currentPixels.filter(pixel => !evictedIds.includes(pixel.id))
          : currentPixels

        return [
          ...retainedPixels,
          {
            id,
            x,
            y,
            color: randomColor(),
            exiting: false,
          },
        ]
      })
    },
    [clearTrailPixelTimeout, gridWidth, removeTrailPixel],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (prefersReducedMotion) {
        return
      }

      const rect = event.currentTarget.getBoundingClientRect()
      const position = getTrailGridPosition(event.clientX, event.clientY, rect, gridWidth, gridHeight)

      if (position === null) {
        return
      }

      if (
        !shouldCreateTrailPixel(
          lastTrailPositionRef.current,
          position,
          prefersReducedMotion,
          activeTrailPositionsRef.current.has(position.y * gridWidth + position.x),
        )
      ) {
        lastTrailPositionRef.current = position
        return
      }

      lastTrailPositionRef.current = position
      addTrailPixel(position.x, position.y)
    },
    [addTrailPixel, gridHeight, gridWidth, prefersReducedMotion],
  )

  const handlePointerLeave = useCallback(() => {
    lastTrailPositionRef.current = null
  }, [])

  useEffect(() => {
    const wrapper = wrapperRef.current
    const parent = wrapper?.parentElement

    if (!parent) {
      return
    }

    const updateGridDimensions = ({height, width}: Pick<DOMRect, 'height' | 'width'>) => {
      const nextDimensions = getGridDimensions(width, height)
      setGridDimensions(currentDimensions =>
        currentDimensions.height === nextDimensions.height && currentDimensions.width === nextDimensions.width
          ? currentDimensions
          : nextDimensions,
      )
    }

    updateGridDimensions(parent.getBoundingClientRect())

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) updateGridDimensions(entry.contentRect)
    })
    resizeObserver.observe(parent)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    clearTrail()
  }, [clearTrail, trailContext])

  useEffect(() => {
    const trailPixelIds = trailPixelIdsRef.current
    const activeTrailPositions = activeTrailPositionsRef.current
    const trailTimeouts = trailTimeoutsRef.current

    return () => {
      for (const {exit, removal} of trailTimeouts.values()) {
        window.clearTimeout(exit)
        window.clearTimeout(removal)
      }
      trailPixelIds.length = 0
      activeTrailPositions.clear()
      trailTimeouts.clear()
    }
  }, [])

  return (
    <div
      className={clsx(Styles.Wrapper, className)}
      onPointerLeave={handlePointerLeave}
      onPointerMove={handlePointerMove}
      ref={wrapperRef}
    >
      {gridWidth > 0 && gridHeight > 0 && (
        <svg
          aria-hidden="true"
          className={Styles.Svg}
          height={gridHeight * CELL_SIZE_PX}
          preserveAspectRatio="none"
          viewBox={`0 0 ${gridWidth} ${gridHeight}`}
          width={gridWidth * CELL_SIZE_PX}
        >
          <defs>
            <pattern id={patternId} height="1" patternUnits="userSpaceOnUse" width="1">
              <path className={Styles.GridLine} d="M 1 0 H 0 V 1" stroke={GRID_LINE_COLOR} />
            </pattern>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor={BACKGROUND_COLOR} stopOpacity="1" />
              <stop offset="0.5" stopColor={BACKGROUND_COLOR} stopOpacity="0" />
              <stop offset="1" stopColor={BACKGROUND_COLOR} stopOpacity="0" />
            </linearGradient>
          </defs>

          <rect fill={BACKGROUND_COLOR} height={gridHeight} width={gridWidth} />
          <rect fill={`url(#${patternId})`} height={gridHeight} width={gridWidth} />
          <path className={Styles.GridLine} d={`M ${gridWidth} 0 V ${gridHeight} H 0`} stroke={GRID_LINE_COLOR} />
          <rect fill={`url(#${gradientId})`} height={gridHeight} width={gridWidth} />
          {trailPixels.map(pixel => (
            <rect
              className={pixel.exiting ? `${Styles.TrailPixel} ${Styles.TrailPixelExiting}` : Styles.TrailPixel}
              fill={pixel.color}
              height={CELL_SIZE}
              key={pixel.id}
              width={CELL_SIZE}
              x={pixel.x}
              y={pixel.y}
            />
          ))}
        </svg>
      )}
    </div>
  )
}
